import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Paper,
  ThemeIcon,
  Stack,
  Box,
  useComputedColorScheme,
} from "@mantine/core";
import {
  IconUsers,
  IconAlertTriangle,
  IconUserCheck,
  IconShieldCheck,
  IconBuilding,
  IconForms,
  IconFileAnalytics,
  IconCertificate,
  IconShield,
  IconPill,
  IconClipboardHeart,
  IconHeartbeat,
  IconCalendarStats,
  IconPlugConnected,
  IconBook2,
  IconListCheck,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { AnimateIn, staggerContainer, fadeUpItem } from "./AnimateIn";

const features = [
  {
    icon: IconUsers,
    title: "Resident Management",
    description:
      "Complete resident profiles with demographics, medical history, care plans, vital signs, appointments, emergency contacts, and insurance. Everything in one place.",
    color: "#2d9cff",
  },
  {
    icon: IconPill,
    title: "Medication Management",
    description:
      "Complete medication lifecycle: scheduling, administration tracking, refusals, holds, discontinuation with full audit trails and compliance reporting.",
    color: "#40c057",
  },
  {
    icon: IconClipboardHeart,
    title: "Care Plans & Progress Notes",
    description:
      "Comprehensive care planning with goal setting, progress tracking, collaborative care coordination, and detailed progress note documentation.",
    color: "#ff6b6b",
  },
  {
    icon: IconHeartbeat,
    title: "Vital Signs & Clinical Tracking",
    description:
      "Track vital signs, manage appointments, assign physicians, monitor pharmacy information, and maintain comprehensive clinical records.",
    color: "#f06595",
  },
  {
    icon: IconAlertTriangle,
    title: "Incident & Case Management",
    description:
      "Report and track incidents with case management, severity classification, resolution workflows, and compliance reporting.",
    color: "#fa5252",
  },
  {
    icon: IconUserCheck,
    title: "Staff Presence & Access Control",
    description:
      "A simple check-in/check-out flow tracks who's on duty at each facility and gates sensitive actions, like medication administration, to staff who are actually present.",
    color: "#fd7e14",
  },
  {
    icon: IconCalendarStats,
    title: "Calendar & Events",
    description:
      "Unified calendar view combining shifts, events, holidays, and appointments with recurring event support and automatic conflict detection.",
    color: "#4c6ef5",
  },
  {
    icon: IconShieldCheck,
    title: "HIPAA Compliance",
    description:
      "Built-in audit logs, PHI protection, role-based access control, and data isolation. Healthcare compliance from day one.",
    color: "#12b886",
  },
  {
    icon: IconBuilding,
    title: "Multi-Facility Support",
    description:
      "Manage multiple facilities from a single dashboard with facility-based data scoping. Perfect for growing organizations.",
    color: "#228be6",
  },
  {
    icon: IconForms,
    title: "Dynamic Forms Engine",
    description:
      "Version-controlled form builder with drag-and-drop fields, resident context binding (DOB, demographics pre-filled), conditional logic, locked submission protection, and PDF-ready output.",
    color: "#e64980",
  },
  {
    icon: IconFileAnalytics,
    title: "Advanced Reporting",
    description:
      "Export to CSV, bulk PDF export for state surveys, generate compliance reports, analyze trends across facilities, and create custom dashboards for insights.",
    color: "#fab005",
  },
  {
    icon: IconCertificate,
    title: "Training & LMS",
    description:
      "Comprehensive learning management with training assignments, assessments, certifications, progress tracking, and completion certificates.",
    color: "#15aabf",
  },
  {
    icon: IconShield,
    title: "Auditor Portal",
    description:
      "External auditor access with time-based grants, audit sessions, findings management, appointment gate-metrics, staffing analysis, and state survey compliance tracking across facilities.",
    color: "#be4bdb",
  },
  {
    icon: IconPlugConnected,
    title: "EMR Integration",
    description:
      "Outbound webhook delivery to EMR/EHR systems for real-time data sync. Configure per-facility integrations with event filtering and reliable delivery guarantees.",
    color: "#20c997",
  },
  {
    icon: IconBook2,
    title: "Compliance Rule Library",
    description:
      "Platform-curated compliance rule bundles covering state regulations. Group homes browse, preview, and import rules directly into their compliance workflows with one click.",
    color: "#9c36b5",
  },
  {
    icon: IconListCheck,
    title: "Role-Aware Task Worklists",
    description:
      "One personalized worklist per caregiver — medications, vitals, ADL, forms, reviews, and compliance — grouped by what's due now and what's overdue. Tasks clear themselves when the work is done in-system, so nothing slips through the cracks.",
    color: "#4c6ef5",
  },
];

export function FeaturesSection() {
  const computedColorScheme = useComputedColorScheme("light");

  return (
    <Box
      component="section"
      id="features"
      py={100}
      bg={computedColorScheme === "dark" ? "#25262B" : "#f8f9fa"}
    >
      <Container size="xl">
        <AnimateIn>
          <Stack align="center" gap="md" mb={60}>
            <Title order={2} size="2.5rem" fw={700} ta="center">
              Everything You Need to{" "}
              <Text
                component="span"
                inherit
                style={{
                  background:
                    "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Run Your Facilities
              </Text>
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={700}>
              Comprehensive features designed specifically for group home and
              assisted living facility management
            </Text>
          </Stack>
        </AnimateIn>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
        >
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <motion.div key={feature.title} variants={fadeUpItem}>
                  <Paper
                    p="xl"
                    radius="md"
                    shadow="sm"
                    style={{
                      height: "100%",
                      transition: "all 0.3s ease",
                      cursor: "default",
                      border: "1px solid #e9ecef",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 24px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <Stack gap="md">
                      <ThemeIcon
                        size={60}
                        radius="md"
                        variant="light"
                        style={{
                          backgroundColor: `${feature.color}15`,
                          color: feature.color,
                        }}
                      >
                        <Icon size={32} stroke={1.5} />
                      </ThemeIcon>
                      <Title order={3} size="h4" fw={600}>
                        {feature.title}
                      </Title>
                      <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                        {feature.description}
                      </Text>
                    </Stack>
                  </Paper>
                </motion.div>
              );
            })}
          </SimpleGrid>
        </motion.div>
      </Container>
    </Box>
  );
}
